import { visit } from 'unist-util-visit';
import type { Element, ElementContent, Root } from 'hast';

/**
 * Rehype transform: give every task-list checkbox an accessible name.
 *
 * GFM renders `- [x] Ship it` as `<li><input type="checkbox" checked disabled> Ship it</li>`.
 * The input has no `<label>` and nothing names it, so a screen reader announces
 * "checkbox, checked" with no hint of *which* task — axe rates it critical. The
 * visible item text is the right name: it is exactly what a sighted reader
 * associates with the box.
 *
 * Runs after sanitize, like the other trusted transforms, so the attribute it
 * adds is our own output rather than something a document could smuggle in.
 * Nested lists are left out of the name: a parent task is named by its own text,
 * not by every subtask under it.
 */
function textOf(nodes: ElementContent[]): string {
  let out = '';
  for (const node of nodes) {
    if (node.type === 'text') out += node.value;
    else if (node.type === 'element') {
      if (node.tagName === 'ul' || node.tagName === 'ol') continue;
      out += textOf(node.children);
    }
  }
  return out;
}

export function rehypeTaskListLabels() {
  return (tree: Root): void => {
    visit(tree, 'element', (li: Element) => {
      if (li.tagName !== 'li') return;
      const input = li.children.find(
        (c): c is Element =>
          c.type === 'element' && c.tagName === 'input' && c.properties?.type === 'checkbox',
      );
      // GFM may wrap a loose task item's content in <p>; look one level down too.
      const box =
        input ??
        li.children
          .filter((c): c is Element => c.type === 'element' && c.tagName === 'p')
          .flatMap((p) => p.children)
          .find(
            (c): c is Element =>
              c.type === 'element' && c.tagName === 'input' && c.properties?.type === 'checkbox',
          );
      if (!box) return;
      const name = textOf(li.children).replace(/\s+/g, ' ').trim();
      if (name) box.properties = { ...box.properties, ariaLabel: name };
    });
  };
}
